import React, { useEffect, useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError, formatGBP, API } from "@/lib/api";
import { LogOut, Search, RefreshCw, ArrowRight, Download, X, Check, Users, BarChart3, MessageCircle, Bell, BellOff } from "lucide-react";
import { LogoMark } from "@/components/Logo";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { AdminFunnelChart } from "@/components/AdminFunnelChart";
import { AdminEnquiriesPanel } from "@/components/AdminEnquiriesPanel";
import { useEnquiryNotifier, ensureNotificationPermission } from "@/hooks/useEnquiryNotifier";
import { toast } from "sonner";

const STATUSES = [
  { id: "all", label: "All" },
  { id: "new", label: "New" },
  { id: "contacted", label: "Contacted" },
  { id: "approved", label: "Approved" },
  { id: "rejected", label: "Rejected" },
];
const BULK_STATUSES = ["new", "contacted", "approved", "rejected"];

export default function AdminDashboard() {
  const { user, ready, logout } = useAuth();
  const nav = useNavigate();
  const [apps, setApps] = useState([]);
  const [stats, setStats] = useState(null);
  const [status, setStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("applications");
  const [lenders, setLenders] = useState([]);
  const isAdmin = user && user.role === "admin";
  const { unread, muted, setMuted, testChime, markAllRead, refresh: refreshEnquiries } = useEnquiryNotifier({ enabled: !!isAdmin });

  // Ask for notification permission once when admin lands on dashboard
  useEffect(() => {
    if (isAdmin) ensureNotificationPermission();
  }, [isAdmin]);

  // If admin is actively viewing the Enquiries tab, keep the badge cleared as new items arrive
  useEffect(() => {
    if (tab === "enquiries" && unread > 0) markAllRead();
  }, [tab, unread, markAllRead]);

  const goToEnquiries = () => {
    setTab("enquiries");
    markAllRead();
  };

  const loadLenders = async () => {
    try {
      const { data } = await api.get("/admin/lenders");
      setLenders(data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const setLenderStatus = async (id, action) => {
    try {
      await api.post(`/admin/lenders/${id}/${action}`);
      toast.success(`Lender ${action === "approve" ? "approved" : "rejected"}`);
      loadLenders();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { if (user && user.role === "admin" && tab === "lenders") loadLenders(); }, [user, tab]);

  const fetchAll = async (s = status) => {
    setLoading(true);
    try {
      const params = s !== "all" ? { status_filter: s } : {};
      const [apsRes, statRes] = await Promise.all([
        api.get("/applications", { params }),
        api.get("/admin/stats"),
      ]);
      setApps(apsRes.data);
      setStats(statRes.data);
      setSelected(new Set());
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (user && user.role === "admin") fetchAll(status); /* eslint-disable-next-line */ }, [status, user]);

  if (!ready) return <div className="min-h-screen bg-navy-deep text-white/50 flex items-center justify-center">Loading…</div>;
  if (!user || user.role !== "admin") return <Navigate to="/admin/login" replace />;

  const filtered = apps.filter((a) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (a.email || "").toLowerCase().includes(q) ||
      (a.company_name || "").toLowerCase().includes(q) ||
      (a.first_name || "").toLowerCase().includes(q) ||
      (a.last_name || "").toLowerCase().includes(q) ||
      (a.id || "").toLowerCase().includes(q)
    );
  });

  const visibleIds = filtered.map((a) => a.id);
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
  const someSelected = visibleIds.some((id) => selected.has(id)) && !allSelected;

  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(visibleIds));
  };
  const toggleOne = (id) => {
    const next = new Set(selected);
    if (next.has(id)) next.delete(id); else next.add(id);
    setSelected(next);
  };

  const bulkUpdate = async (newStatus) => {
    if (selected.size === 0) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/applications-bulk-update", {
        ids: Array.from(selected),
        status: newStatus,
      });
      toast.success(`${data.updated} application${data.updated === 1 ? "" : "s"} marked as ${newStatus}`);
      await fetchAll(status);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const exportCSV = async () => {
    setBusy(true);
    try {
      const params = status !== "all" ? { status_filter: status } : {};
      const res = await api.get("/admin/applications-export.csv", { params, responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const filename = `swiftcash-applications-${new Date().toISOString().slice(0, 10)}.csv`;
      const a = document.createElement("a");
      a.href = url; a.download = filename; document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-navy-deep text-white pb-32">
      <header className="border-b border-white/10 sticky top-0 z-30 bg-navy-deep/95 backdrop-blur-xl">
        <div className="max-w-[1440px] mx-auto px-5 md:px-8 h-[72px] flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <LogoMark size={38} />
            <div>
              <div className="font-display font-extrabold text-[18px] tracking-[-0.035em]">
                Swift<span className="text-emerald">Cash</span>
              </div>
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/45 mt-0.5">Admin</div>
            </div>
          </Link>
          <div className="flex items-center gap-4">
            <span className="hidden md:block font-mono text-[11px] uppercase tracking-[0.15em] text-white/45 font-semibold">{user.email}</span>
            <button
              type="button"
              onClick={() => {
                const next = !muted;
                setMuted(next);
                if (!next) testChime(); // demo the chime when re-enabling
                toast.success(next ? "Enquiry chime muted" : "Enquiry chime on");
              }}
              data-testid="admin-chime-toggle"
              aria-label={muted ? "Unmute enquiry chime" : "Mute enquiry chime"}
              title={muted ? "Enquiry chime is muted — click to enable" : "Enquiry chime is on — click to mute"}
              className={`inline-flex items-center justify-center h-9 w-9 rounded-full border transition-colors ${
                muted
                  ? "border-white/15 bg-white/5 text-white/45 hover:text-white hover:bg-white/10"
                  : "border-emerald/40 bg-emerald/15 text-emerald hover:bg-emerald/25"
              }`}
            >
              {muted ? <BellOff className="h-4 w-4" strokeWidth={2.4} /> : <Bell className="h-4 w-4" strokeWidth={2.4} />}
            </button>
            <button
              onClick={() => { logout(); nav("/"); }}
              data-testid="admin-logout-btn"
              className="inline-flex items-center gap-1.5 text-[13px] font-bold text-white/70 hover:text-white"
            >
              <LogOut className="h-4 w-4" strokeWidth={2.4} /> Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1440px] mx-auto px-5 md:px-8 py-10 md:py-14 space-y-10">
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald/15 border border-emerald/30 text-emerald font-mono text-[11px] uppercase tracking-[0.12em] font-bold mb-4">
              Pipeline
            </div>
            <h1 className="font-display font-extrabold text-[44px] md:text-[60px] leading-[0.95] tracking-[-0.04em]">
              Applications
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={exportCSV}
              disabled={busy}
              data-testid="admin-export-csv-btn"
              className="inline-flex items-center gap-2 h-11 px-5 rounded-lg border border-emerald/40 bg-emerald/10 text-emerald hover:bg-emerald/15 text-[13px] font-bold disabled:opacity-50"
            >
              <Download className="h-4 w-4" strokeWidth={2.4} /> Export CSV
            </button>
            <button onClick={() => fetchAll()} data-testid="admin-refresh-btn" className="inline-flex items-center gap-2 h-11 px-5 rounded-lg border border-white/15 bg-white/5 text-white hover:bg-white/10 text-[13px] font-bold">
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} strokeWidth={2.4} /> Refresh
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="admin-stats">
          <StatCard label="Total" value={stats?.total ?? "—"} />
          <StatCard label="New" value={stats?.by_status?.new ?? "—"} accent="bg-amber/15 text-amber" />
          <StatCard label="Approved" value={stats?.by_status?.approved ?? "—"} accent="bg-emerald/15 text-emerald" />
          <StatCard label="£ Requested" value={stats ? formatGBP(stats.total_amount_requested) : "—"} />
        </div>

        <div className="flex flex-col md:flex-row md:items-center gap-4 md:gap-5">
          <div className="inline-flex items-center gap-1 p-1 bg-white/5 border border-white/10 rounded-full">
            <button onClick={() => setTab("applications")} data-testid="admin-tab-applications"
              className={`px-4 py-1.5 rounded-full text-[13px] font-bold transition-colors ${tab === "applications" ? "bg-white text-navy" : "text-white/60 hover:text-white"}`}>
              Applications
            </button>
            <button onClick={() => setTab("lenders")} data-testid="admin-tab-lenders"
              className={`px-4 py-1.5 rounded-full text-[13px] font-bold transition-colors inline-flex items-center gap-1.5 ${tab === "lenders" ? "bg-white text-navy" : "text-white/60 hover:text-white"}`}>
              <Users className="h-3.5 w-3.5" strokeWidth={2.4} /> Lenders
            </button>
            <button onClick={goToEnquiries} data-testid="admin-tab-enquiries"
              className={`relative px-4 py-1.5 rounded-full text-[13px] font-bold transition-colors inline-flex items-center gap-1.5 ${tab === "enquiries" ? "bg-white text-navy" : "text-white/60 hover:text-white"}`}>
              <MessageCircle className="h-3.5 w-3.5" strokeWidth={2.4} /> Enquiries
              {unread > 0 && (
                <span
                  data-testid="enquiries-unread-badge"
                  className={`ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full text-[10px] font-mono font-extrabold px-1.5 ${
                    tab === "enquiries" ? "bg-emerald text-white" : "bg-emerald text-white animate-pulse"
                  }`}
                >
                  {unread > 99 ? "99+" : unread}
                </span>
              )}
            </button>
            <button onClick={() => setTab("analytics")} data-testid="admin-tab-analytics"
              className={`px-4 py-1.5 rounded-full text-[13px] font-bold transition-colors inline-flex items-center gap-1.5 ${tab === "analytics" ? "bg-white text-navy" : "text-white/60 hover:text-white"}`}>
              <BarChart3 className="h-3.5 w-3.5" strokeWidth={2.4} /> Analytics
            </button>
          </div>
          {tab === "applications" && (
            <>
              <div className="inline-flex items-center gap-1 p-1 bg-white/5 border border-white/10 rounded-full">
                {STATUSES.map((s) => (
                  <button
                    key={s.id} onClick={() => setStatus(s.id)} data-testid={`admin-filter-${s.id}`}
                    className={`px-4 py-1.5 rounded-full text-[13px] font-bold transition-colors
                      ${status === s.id ? "bg-white text-navy" : "text-white/60 hover:text-white"}`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>
              <div className="relative flex-1 max-w-sm md:ml-auto">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" strokeWidth={2.4} />
                <Input
                  placeholder="Search name, email or ID"
                  value={search} onChange={(e) => setSearch(e.target.value)}
                  className="pl-10 bg-white/5 border-white/15 text-white placeholder:text-white/30 h-11 rounded-full font-semibold"
                  data-testid="admin-search"
                />
              </div>
            </>
          )}
        </div>

        <div className="rounded-[20px] border border-white/10 overflow-hidden bg-white/[0.02]" style={{ display: tab === "applications" ? "block" : "none" }}>
          <div className="grid grid-cols-12 gap-4 px-6 py-4 bg-white/[0.03] border-b border-white/10 font-mono text-[10.5px] uppercase tracking-[0.12em] font-bold text-white/45 items-center">
            <div className="col-span-1 flex items-center">
              <Checkbox
                checked={allSelected ? true : (someSelected ? "indeterminate" : false)}
                onCheckedChange={toggleAll}
                data-testid="admin-select-all"
                className="border-white/30 data-[state=checked]:bg-emerald data-[state=checked]:border-emerald data-[state=indeterminate]:bg-emerald data-[state=indeterminate]:border-emerald"
              />
            </div>
            <div className="col-span-3">Applicant</div>
            <div className="col-span-2">Purpose</div>
            <div className="col-span-2">Amount</div>
            <div className="col-span-2">Submitted</div>
            <div className="col-span-2 text-right">Status</div>
          </div>
          {filtered.length === 0 && (
            <div className="px-5 py-16 text-center text-white/50 text-sm font-semibold" data-testid="admin-empty-state">
              {loading ? "Loading applications…" : "No applications yet."}
            </div>
          )}
          {filtered.map((a) => {
            const isSelected = selected.has(a.id);
            return (
              <div
                key={a.id}
                data-testid={`admin-row-${a.id}`}
                className={`grid grid-cols-12 gap-4 px-6 py-5 border-b border-white/5 last:border-0 transition-colors items-center group
                  ${isSelected ? "bg-emerald/[0.06]" : "hover:bg-white/[0.03]"}`}
              >
                <div className="col-span-1 flex items-center">
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => toggleOne(a.id)}
                    data-testid={`admin-row-checkbox-${a.id}`}
                    className="border-white/30 data-[state=checked]:bg-emerald data-[state=checked]:border-emerald"
                  />
                </div>
                <button onClick={() => nav(`/admin/applications/${a.id}`)} className="col-span-3 text-left">
                  <div className="font-display font-extrabold text-[16px] text-white tracking-[-0.015em] truncate">{a.company_name || `${a.first_name || ""} ${a.last_name || ""}`.trim() || "—"}</div>
                  <div className="text-[12.5px] text-white/45 truncate mt-0.5 font-mono">{a.email}</div>
                </button>
                <div className="col-span-2 text-[13.5px] text-white/70 truncate font-semibold">{a.purpose}</div>
                <div className="col-span-2 font-display font-extrabold text-[20px] text-white tracking-[-0.03em]">{formatGBP(a.amount)}</div>
                <div className="col-span-2 text-[13px] text-white/55 font-mono">
                  {new Date(a.submitted_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}
                </div>
                <div className="col-span-2 flex items-center justify-end gap-2">
                  <StatusBadge status={a.status} />
                  <button onClick={() => nav(`/admin/applications/${a.id}`)} className="text-white/30 hover:text-white" data-testid={`admin-row-open-${a.id}`}>
                    <ArrowRight className="h-4 w-4" strokeWidth={2.4} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Lenders panel */}
        {tab === "lenders" && (
          <div className="rounded-[20px] border border-white/10 overflow-hidden bg-white/[0.02]" data-testid="admin-lenders-panel">
            <div className="grid grid-cols-12 gap-4 px-6 py-4 bg-white/[0.03] border-b border-white/10 font-mono text-[10.5px] uppercase tracking-[0.12em] font-bold text-white/45 items-center">
              <div className="col-span-4">Company</div>
              <div className="col-span-3">Contact</div>
              <div className="col-span-3">Products</div>
              <div className="col-span-2 text-right">Status</div>
            </div>
            {lenders.length === 0 && (
              <div className="px-5 py-16 text-center text-white/50 text-sm font-semibold">No lenders yet. Share <span className="font-mono">/lender/signup</span> with your panel.</div>
            )}
            {lenders.map((l) => (
              <div key={l.id} data-testid={`lender-row-${l.id}`}
                className="grid grid-cols-12 gap-4 px-6 py-5 border-b border-white/5 last:border-0 hover:bg-white/[0.03] items-center">
                <div className="col-span-4">
                  <div className="font-display font-extrabold text-[15.5px] text-white tracking-[-0.015em]">{l.company}</div>
                  <div className="text-[12px] text-white/45 font-mono truncate">{l.email}</div>
                </div>
                <div className="col-span-3 text-[13px] text-white/80 font-semibold">
                  {l.name}
                  <div className="text-[11.5px] text-white/45 font-mono mt-0.5">{l.phone || "—"}</div>
                </div>
                <div className="col-span-3 text-[12px] text-white/60 font-medium truncate">
                  {(l.panel_products || []).join(", ") || "—"}
                </div>
                <div className="col-span-2 flex items-center justify-end gap-2">
                  {l.status === "approved" && (
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full border border-emerald/30 bg-emerald/15 text-emerald text-[10px] font-mono font-extrabold uppercase tracking-[0.1em]">approved</span>
                  )}
                  {l.status === "pending" && (
                    <>
                      <button onClick={() => setLenderStatus(l.id, "approve")} data-testid={`lender-approve-${l.id}`}
                        className="inline-flex items-center gap-1 h-8 px-3 rounded-full bg-emerald text-white text-[11px] font-extrabold hover:bg-emerald-2">
                        <Check className="h-3 w-3" strokeWidth={3} /> Approve
                      </button>
                      <button onClick={() => setLenderStatus(l.id, "reject")} data-testid={`lender-reject-${l.id}`}
                        className="inline-flex items-center gap-1 h-8 px-3 rounded-full border border-white/15 text-white/70 text-[11px] font-bold hover:bg-white/5">
                        <X className="h-3 w-3" strokeWidth={3} /> Reject
                      </button>
                    </>
                  )}
                  {l.status === "rejected" && (
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full border border-coral/40 bg-coral/15 text-coral text-[10px] font-mono font-extrabold uppercase tracking-[0.1em]">rejected</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Analytics panel */}
        {tab === "analytics" && <AdminFunnelChart />}

        {/* Enquiries panel */}
        {tab === "enquiries" && <AdminEnquiriesPanel />}
      </main>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div
          data-testid="admin-bulk-bar"
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 w-[calc(100%-2rem)] max-w-3xl rounded-2xl bg-navy border border-emerald/40 shadow-emerald p-4 md:p-5"
        >
          <div className="flex flex-wrap items-center gap-3 md:gap-4">
            <div className="flex items-center gap-3 mr-auto">
              <div className="h-10 w-10 rounded-xl bg-emerald text-white flex items-center justify-center font-display font-extrabold text-[15px]">
                {selected.size}
              </div>
              <div>
                <div className="font-display font-extrabold text-[15px] text-white tracking-[-0.015em]" data-testid="bulk-count">
                  {selected.size} selected
                </div>
                <div className="text-[12px] text-white/55 font-semibold">Mark as…</div>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {BULK_STATUSES.map((s) => (
                <button
                  key={s}
                  onClick={() => bulkUpdate(s)}
                  disabled={busy}
                  data-testid={`bulk-status-${s}`}
                  className="inline-flex items-center gap-1.5 h-10 px-4 rounded-lg border border-white/15 bg-white/5 hover:bg-emerald hover:border-emerald hover:text-white text-white/85 text-[12.5px] font-bold capitalize disabled:opacity-50 transition-colors"
                >
                  <Check className="h-3.5 w-3.5" strokeWidth={3} />
                  {s}
                </button>
              ))}
              <button
                onClick={() => setSelected(new Set())}
                disabled={busy}
                data-testid="bulk-clear"
                className="inline-flex items-center justify-center h-10 w-10 rounded-lg border border-white/15 text-white/60 hover:text-white hover:bg-white/5 disabled:opacity-50"
                aria-label="Clear selection"
              >
                <X className="h-4 w-4" strokeWidth={2.4} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, accent = "bg-white/10 text-white" }) {
  return (
    <div className="rounded-[20px] border border-white/10 bg-white/[0.02] p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="font-mono text-[10.5px] uppercase tracking-[0.12em] font-bold text-white/45">{label}</div>
        <div className={`h-6 w-6 rounded-full ${accent} flex items-center justify-center`}>
          <span className="h-1.5 w-1.5 rounded-full bg-current" />
        </div>
      </div>
      <div className="font-display font-extrabold text-[36px] md:text-[44px] leading-none tracking-[-0.04em] text-white">{value}</div>
    </div>
  );
}

export function StatusBadge({ status }) {
  const map = {
    new: "bg-amber/15 text-amber border-amber/30",
    contacted: "bg-sky-400/10 text-sky-300 border-sky-400/30",
    approved: "bg-emerald/15 text-emerald border-emerald/30",
    rejected: "bg-coral/15 text-coral border-coral/40",
  };
  const cls = map[status] || "bg-white/10 text-white/70 border-white/15";
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full border text-[10px] font-mono font-extrabold uppercase tracking-[0.1em] ${cls}`}>
      {status}
    </span>
  );
}
